import type { SchemaInput } from './schema-input.js';
import { isObject } from './runtime-values.js';
import type { DataLayer } from './data-layer.js';

function freeze(value: SchemaInput): void {
  if (!value || !isObject(value) || Object.isFrozen(value)) return;
  Object.freeze(value);
  for (const child of Object.values(value)) freeze(child);
}

/** Feature records are immutable between edits. History shares unchanged records. */
export function copyDataLayer(layer: DataLayer, editedIds: ReadonlySet<string> = new Set()): DataLayer {
  const { features, ...metadata } = layer;
  return {
    ...structuredClone(metadata),
    features: features.map(feature => {
      if (editedIds.has(feature.id)) return structuredClone(feature);
      freeze(feature);
      return feature;
    }),
  };
}

const featureStamps = new WeakMap<object, string>();
function stamp(text: string): string {
  let a = 0x811c9dc5; let b = 0x9e3779b9;
  for (let i = 0; i < text.length; i++) {
    const value = text.charCodeAt(i);
    a = Math.imul(a ^ value, 0x01000193);
    b = Math.imul(b ^ value, 0x85ebca6b);
  }
  return `${text.length}:${a >>> 0}:${b >>> 0}`;
}

/** A compact dirty-state signature, not a security or integrity checksum. */
export function dataLayerContentStamp(layer: DataLayer): string {
  const { features, saved: _saved, persisted: _persisted, draftId: _draftId, serverVersion: _version, retainedSources, ...metadata } = layer;
  const parts = [JSON.stringify(metadata), JSON.stringify(retainedSources?.map(source => [source.name, source.data.size]))];
  for (const feature of features) {
    let value = Object.isFrozen(feature) ? featureStamps.get(feature) : undefined;
    if (!value) {
      value = stamp(JSON.stringify(feature));
      if (Object.isFrozen(feature)) featureStamps.set(feature, value);
    }
    parts.push(value);
  }
  return stamp(parts.join('|'));
}
